/**
 * HEPS Deployment app handoff — the pending state shared by /app-auth, login/index.vue and
 * main.ts. No imports (main.ts needs it before the router). The MSAL id token is carried in
 * MEMORY ONLY; only the opaque c/s/r values go to sessionStorage (spec §6.2). MSAL's own
 * sessionStorage cache also holds the id token between the redirect and /app-auth?resume=1,
 * which clears that cache as soon as it has taken the token.
 */
export const APP_AUTH_PATH = '/app-auth';
/** MSAL `state` on the /app-auth loginRedirect; comes back as AuthenticationResult.state. */
export const APP_AUTH_STATE = 'hepsdeploy-app-auth';
export const APP_AUTH_KEYS = {
  c: 'APP_AUTH_C',
  s: 'APP_AUTH_S',
  r: 'APP_AUTH_R',
  at: 'APP_AUTH_AT',
} as const;
const FRESH_MS = 10 * 60 * 1000;

let pendingIdToken: string | null = null;
let pendingUsername: string | null = null;

export function savePendingAppAuth(c: string, s: string, r: string): void {
  sessionStorage.setItem(APP_AUTH_KEYS.c, c);
  sessionStorage.setItem(APP_AUTH_KEYS.s, s);
  sessionStorage.setItem(APP_AUTH_KEYS.r, r);
  sessionStorage.setItem(APP_AUTH_KEYS.at, String(Date.now()));
}

export function clearPendingAppAuth(): void {
  for (const key of Object.values(APP_AUTH_KEYS)) sessionStorage.removeItem(key);
  pendingIdToken = null;
  pendingUsername = null;
}

/** True only while a handoff started less than 10 minutes ago is waiting for a login. */
export function hasPendingAppAuth(): boolean {
  try {
    const c = sessionStorage.getItem(APP_AUTH_KEYS.c);
    const at = Number(sessionStorage.getItem(APP_AUTH_KEYS.at) || 0);
    if (!c) return false;
    if (!at || Date.now() - at > FRESH_MS) {
      clearPendingAppAuth();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function readPendingAppAuth(): { c: string; s: string; r: string } | null {
  if (!hasPendingAppAuth()) return null;
  return {
    c: sessionStorage.getItem(APP_AUTH_KEYS.c) || '',
    s: sessionStorage.getItem(APP_AUTH_KEYS.s) || '',
    r: sessionStorage.getItem(APP_AUTH_KEYS.r) || '',
  };
}

export function setPendingIdToken(idToken?: string, username?: string): void {
  pendingIdToken = idToken || null;
  pendingUsername = username || null;
}

/** Returns the MSAL result once and forgets it. */
export function takePendingIdToken(): { idToken: string; username: string } | null {
  const value =
    pendingIdToken && pendingUsername
      ? { idToken: pendingIdToken, username: pendingUsername }
      : null;
  pendingIdToken = null;
  pendingUsername = null;
  return value;
}
